import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { TableCellContainer } from '@/features/shared/components/tableSystem/TableCells';
import { CLONE_TOOLTIP_SLOT_PROPS } from '@/features/shared/components/tableSystem/TableMetaCells';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { Box, CodeOutputBlock, Tooltip, useMediaQuery, useTheme } from '@/ui';
import { TABLE_TOOLTIP_ENTER_DELAY_MS } from '@/ui/composites/table/tableTooltip';
import type { SxProps, Theme } from '@/ui/primitives';

type UploadErrorTableCellProps = {
  error: string | null;
};

const CLONE_TOOLTIP_BASE_SX: SxProps<Theme> = {
  fontSize: '15px',
  lineHeight: 1.4,
};

export const UploadErrorTableCell = ({ error }: UploadErrorTableCellProps): ReactElement => {
  const { notify } = useNotifications();
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('md'));
  const outputRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const formattedError = error?.trim().length ? error : '-';
  const tooltipPlacement = 'top-start';
  const fallbackPlacements = isNarrowScreen
    ? ['bottom-start', 'bottom', 'bottom-end']
    : ['top', 'top-end', 'bottom-start', 'bottom', 'bottom-end'];

  const updateTruncationState = useCallback((): void => {
    const node = outputRef.current;
    if (!node) {
      return;
    }

    const textNode = node.querySelector('.MuiTypography-root');
    const textElement = textNode instanceof HTMLElement ? textNode : null;

    if (!textElement) {
      setIsTruncated(false);
      return;
    }

    const nextIsTruncated =
      textElement.scrollHeight > textElement.clientHeight ||
      textElement.scrollWidth > textElement.clientWidth;
    setIsTruncated(nextIsTruncated);
  }, []);

  useEffect(() => {
    updateTruncationState();

    const node = outputRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateTruncationState();
    });

    observer.observe(node);

    const textNode = node.querySelector('.MuiTypography-root');
    if (textNode instanceof HTMLElement) {
      observer.observe(textNode);
    }

    return () => {
      observer.disconnect();
    };
  }, [formattedError, updateTruncationState]);

  const handleCopyError = useCallback((): void => {
    if (!error?.trim()) {
      return;
    }

    void navigator.clipboard.writeText(error);
    notify({
      message: getToastMessage('clipboard.copied', { label: 'Error' }),
      tone: 'green',
    });
  }, [error, notify]);

  return (
    <TableCellContainer gap="none">
      <Tooltip
        title={
          <CodeOutputBlock
            value={formattedError}
            copyAriaLabel="Copy error"
            onCopy={handleCopyError}
            reserveCopySpace={false}
            background="transparent"
            sx={{
              display: 'inline-block',
              width: 'fit-content',
              maxWidth: 'min(1080px, calc(100vw - 24px))',
              backgroundColor: 'transparent',
              border: 'none',
              '& .MuiPaper-root, &.MuiPaper-root': {
                backgroundColor: 'transparent',
                border: 'none',
              },
            }}
            contentSx={{
              fontSize: '15px',
              maxHeight: 'min(60vh, 480px)',
              overflowX: 'auto',
              overflowY: 'auto',
              overflowWrap: 'anywhere',
            }}
          />
        }
        disableHoverListener={!isTruncated}
        disableInteractive={false}
        placement={tooltipPlacement}
        enterDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
        enterNextDelay={TABLE_TOOLTIP_ENTER_DELAY_MS}
        arrow
        slotProps={{
          ...CLONE_TOOLTIP_SLOT_PROPS,
          popper: {
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements,
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  altAxis: true,
                  padding: 12,
                },
              },
            ],
            sx: {
              pointerEvents: 'auto',
            },
          },
          arrow: {
            sx: {
              color: (tooltipTheme) => tooltipTheme.app.colors.surface,
            },
          },
          tooltip: {
            sx: [
              CLONE_TOOLTIP_BASE_SX,
              {
                width: 'fit-content',
                maxWidth: 'none',
                p: 0,
                backgroundColor: (tooltipTheme) => tooltipTheme.app.colors.surface,
                boxShadow: 'none',
              },
            ],
          },
        }}
      >
        <Box component="span" ref={outputRef} sx={{ display: 'block', width: '100%' }}>
          <CodeOutputBlock
            value={formattedError}
            background="transparent"
            sx={{
              width: '100%',
              minWidth: 0,
              backgroundColor: 'transparent',
              border: 'none',
              '& .MuiPaper-root, &.MuiPaper-root': {
                backgroundColor: 'transparent',
                border: 'none',
              },
              '& .MuiBox-root': {
                px: 0,
                py: 0,
              },
            }}
            contentSx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 4,
              wordBreak: 'break-word',
              '&&': {
                fontSize: '13px',
                lineHeight: 1.4,
              },
            }}
          />
        </Box>
      </Tooltip>
    </TableCellContainer>
  );
};
